type ScrollDirection = "horizontal" | "vertical";

export const getElementScrollDirection = (
  element: HTMLElement,
): ScrollDirection | undefined => {
  const hasVerticalScroll = element.scrollHeight > element.clientHeight;
  const hasHorizontalScroll = element.scrollWidth > element.clientWidth;

  if (hasVerticalScroll === hasHorizontalScroll) {
    return undefined;
  }

  return hasHorizontalScroll ? "horizontal" : "vertical";
};

export const getElementsKeysAndSizes = (
  container: HTMLElement,
  element: HTMLElement,
  direction: ScrollDirection,
): {
  elementSize: number;
  containerSize: number;
  offsetKey: Extract<keyof HTMLElement, "offsetLeft" | "offsetTop">;
  scrollKey: Extract<keyof HTMLElement, "scrollLeft" | "scrollTop">;
  scrollToKey: Extract<keyof ScrollToOptions, "top" | "left">;
} => {
  if (direction === "horizontal") {
    return {
      elementSize: element?.offsetWidth || 0,
      containerSize: container?.offsetWidth || 0,
      offsetKey: "offsetLeft",
      scrollKey: "scrollLeft",
      scrollToKey: "left",
    };
  }
  return {
    elementSize: element?.offsetHeight || 0,
    containerSize: container?.offsetHeight || 0,
    offsetKey: "offsetTop",
    scrollKey: "scrollTop",
    scrollToKey: "top",
  };
};

interface ScrollElementIntoViewWithinContainerProps {
  behavior?: ScrollBehavior;
  container: HTMLElement;
  direction: ScrollDirection;
  element: HTMLElement;
  mode?: "default" | "middle" | "edge";
  offset?: number;
}

export const scrollElementIntoViewWithinContainer = ({
  element,
  container,
  offset = 0,
  mode = "default",
  behavior = "smooth",
  direction,
}: ScrollElementIntoViewWithinContainerProps) => {
  const { elementSize, containerSize, scrollToKey, scrollKey, offsetKey } =
    getElementsKeysAndSizes(container, element, direction);

  const isFullyVisible =
    element[offsetKey] >= container[scrollKey] + offset &&
    element[offsetKey] + elementSize + offset <
      containerSize + container[scrollKey];

  if (isFullyVisible && mode === "edge") {
    return;
  } else if (mode === "middle" || isFullyVisible) {
    container.scrollTo({
      behavior,
      [scrollToKey]: element[offsetKey] - (containerSize - elementSize) / 2,
    });
  } else if (
    containerSize < elementSize ||
    container[scrollKey] + offset >= element[offsetKey]
  ) {
    container.scrollTo({
      behavior,
      [scrollToKey]: element[offsetKey] - offset,
    });
  } else {
    container.scrollTo({
      behavior,
      [scrollToKey]: element[offsetKey] - containerSize + elementSize + offset,
    });
  }
};

interface scrollElementToTheEdgeOfContainerProps {
  behavior?: ScrollBehavior;
  container: HTMLElement;
  direction: ScrollDirection;
  element: HTMLElement;
  offset?: number;
}

export const scrollElementToTheStartOfContainer = ({
  behavior = "smooth",
  container,
  direction,
  element,
  offset = 0,
}: scrollElementToTheEdgeOfContainerProps) => {
  const { scrollToKey, offsetKey } = getElementsKeysAndSizes(
    container,
    element,
    direction,
  );

  container.scrollTo({
    behavior,
    [scrollToKey]: element[offsetKey] - offset,
  });
};

export function scrollIntoViewAndWait(
  el: Element,
  options?: ScrollIntoViewOptions,
  timeoutMs = 3000,
  failOnTimeout = false,
): Promise<void> {
  return new Promise((resolve, reject) => {
    let lastPosition: number | null = null;
    let sameCount = 0;
    let resolved = false;

    function finish(result: "resolve" | "reject") {
      resolved = true;
      result === "resolve"
        ? resolve()
        : reject(new Error("scrollIntoView timed out"));
    }

    function checkScroll() {
      if (resolved) return;

      const currentPosition = window.scrollY;

      if (currentPosition === lastPosition) {
        sameCount++;
        if (sameCount > 5) {
          finish("resolve");
          return;
        }
      } else {
        sameCount = 0;
        lastPosition = currentPosition;
      }

      requestAnimationFrame(checkScroll);
    }

    setTimeout(() => {
      if (!resolved) {
        finish(failOnTimeout ? "reject" : "resolve");
      }
    }, timeoutMs);

    el.scrollIntoView(options);
    requestAnimationFrame(checkScroll);
  });
}
